import React, { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 → target when first scrolled into view.
 * For non-numeric labels (e.g., "Any", "AI-first") we render as-is.
 */
export const AnimatedCounter = ({ value, duration = 1200, suffix = "" }) => {
    const ref = useRef(null);
    const [display, setDisplay] = useState(null);

    const isNumeric = typeof value === "number";

    useEffect(() => {
        if (!isNumeric) {
            setDisplay(value);
            return undefined;
        }
        const el = ref.current;
        if (!el) return undefined;
        let raf;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    const start = performance.now();
                    const step = (now) => {
                        const p = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - p, 3);
                        setDisplay(Math.round(value * eased));
                        if (p < 1) raf = requestAnimationFrame(step);
                    };
                    raf = requestAnimationFrame(step);
                    io.unobserve(el);
                }
            },
            { threshold: 0.4 }
        );
        io.observe(el);
        return () => {
            io.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [value, duration, isNumeric]);

    return (
        <span ref={ref} className="animated-counter" data-testid="animated-counter">
            {isNumeric ? `${display ?? 0}${suffix}` : display ?? value}
        </span>
    );
};

export default AnimatedCounter;
